export enum SurveyName {
  ADMIN_PANEL = 'adminpanel',
  AUDIT = 'audit',
  ENTIRE_PROGRAM = 'entireprogram',
}

/**
 * Get the file name for a Distro file in S3
 * 
 * @param program - Either a `Program` object or a string representing the program
 * @param surveyName - The name of the survey
 * @param options.collabOpsFile - Whether this represents a collab ops file or a non-collab survey file. Defaults to false.
 * @param options.branch - Optional Distro branch of the survey. Only used if `collabOpsFile` is true.
 * @param options.withoutFileExtension - Whether to _exclude_ the trailing `.json`. Defaults to false (include the `.json`).
 * 
 * @returns The formatted Distro file name.
 * 
 * This can take the following forms:
 * @example 
 * // Non-collab with file extension:
 * `dso-<program>-<survey>.json`
 * 
 * // Non-collab without file extension:
 * `dso-<program>-survey`
 * 
 * // Collab without a branch, with file extension:
 * `dso-<program>-<survey>.ops.json`
 * 
 * // Collab without a branch, without file extension:
 * `dso-<program>-<survey>.ops`
 * 
 * // Collab with a branch, with file extension:
 * `.branches/dso-<program>-<survey>-<branch>.ops.json`
 * 
 * // Collab with a branch, without file extension:
 * `.branches/dso-<program>-<survey>-<branch>.ops`
 */
export const getDistroFileName = (
  program: { name: string } | string,
  surveyName: string,
  options: {
    collabOpsFile?: boolean,
    branch?: number;
    withoutFileExtension?: boolean;
  } = {}) => {
  const programName = typeof program === 'object' ? program.name : program;

  const branch = options.branch ? `-${options.branch}` : '';  
  const collabOpsFile = options.collabOpsFile ? `${branch}.ops` : '';
  const branchPrefix = options.collabOpsFile && branch ? '.branches/' : '';

  const fileExtension = options.withoutFileExtension ? '' : '.json';

  return `${branchPrefix}dso-${programName}-${surveyName}${collabOpsFile}${fileExtension}`;
};
